/**
 * The world: three.js over the game store. It reads the store and the local walker, draws the
 * planet, props, figures, clouds and sparks, and never talks to the network or the HUD. This file
 * puts the parts together and runs the frame; each part lives in its own folder.
 */
import { RULES } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { logger } from '@core/log';
import type { Loop } from '@core/loop';
import type { Game } from '@game/game';
import { inviteTo, type Invite } from '@game/session/invite';

import { createFigures } from './actors/figures';
import { loadArt } from './assets/load';
import { createMates } from './actors/mates';
import { createMe } from './actors/me';
import { createEffects } from './effects/effects';
import { reactToGame } from './effects/reactions';
import { createHubBeacon } from './overlays/hubBeacon';
import { createProjector, screenPoint } from './overlays/projector';
import { createCursor } from './placement/cursor';
import { createDisplay } from './planet/display';
import { pickTile } from './planet/pick';
import { createPlanet } from './planet/planet';
import type { PlanetPose } from './planet/pose';
import { radiusOf } from './planet/view';
import { createProps } from './props';
import { createGrass } from './props/grass';
import { createCameraRig } from './stage/camera';
import { createClouds } from './stage/clouds';
import { createRenderer } from './stage/renderer';
import { createScene } from './stage/scene';
import { createSparks } from './stage/sparks';
import { QUALITY, type Look } from './style/look';
import { createQualityGovernor, START_TIER } from './style/quality';
import { setMotion, updateStyle } from './style/uniforms';

export interface World {
  /** One frame; `full` when the loop runs at full rate (the quality tier is judged on those). */
  frame(dt: number, now: number, full: boolean): void;
  /** The tile under a screen position, or -1. */
  pickTile(x: number, y: number): number;
  /** Where my figure is on screen, for taps on myself. */
  myScreenPosition(): { x: number; y: number };
  /**
   * Radians the planet turns per finger pixel: the same number of hexagons per swipe on every
   * planet size (it feels like size 1) and at every zoom.
   */
  fingerScale(): number;
  shownRadius(): number;
  stats(): { calls: number; triangles: number; frameMs: number; quality: string };
  resize(): void;
  dispose(): void;
}

export interface WorldLook {
  nameOf: (player: string) => string;
  myColor: () => string;
}

const UP = new THREE.Vector3(0, 1, 0);
/** Where a tap counts as a tap on me: a little above my feet. */
const MY_MIDDLE = new THREE.Vector3(0, 0.4, 0);
const log = logger('world');
/** Grass tufts grow this many steps around me (the stylized look). */
const GRASS_REACH = 4;

export function createWorld(
  canvas: HTMLCanvasElement,
  tagsRoot: HTMLElement,
  game: Game,
  loop: Loop,
  look: WorldLook,
  style: Look = 'classic',
): World {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stylized = style === 'stylized';
  const governor = stylized ? createQualityGovernor(START_TIER) : null;
  let quality = QUALITY[governor?.tier ?? START_TIER];
  const renderer = createRenderer(canvas);
  const world = createScene();
  if (stylized) {
    renderer.setLook(style, quality.pixelRatio);
    world.sun.castShadow = false;
    setMotion(!reduced && quality.wind, !reduced && quality.movingWater);
  }
  const planet = createPlanet(world.planet, style, quality.detail);
  const props = createProps(world.props, style);
  const grass = stylized ? createGrass(world.props) : null;
  if (stylized) {
    // the art (docs/ART.md) replaces its stand-ins as soon as it has loaded
    void loadArt().then((art) => {
      props.setArt(art.geometries);
      for (const problem of art.problems) log.warn(`art: ${problem}`);
      loop.wake();
    });
  }
  const figures = createFigures(world.scene, stylized ? 'spot' : 'cast');
  const clouds = createClouds(world.sky);
  const sparks = createSparks(world.planet, reduced);
  const rig = createCameraRig(world);
  const projector = createProjector(world.camera);
  const display = createDisplay(game, planet, props);
  const effects = createEffects(planet, props, sparks, display, loop);
  const me = createMe(game, figures, planet, look.myColor, reduced);
  const mates = createMates({
    game,
    figures,
    planet,
    projector,
    root: tagsRoot,
    nameOf: look.nameOf,
    loop,
    reduced,
  });
  const beacon = createHubBeacon(tagsRoot, projector, planet);
  const cursor = createCursor(game, world.planet, display, planet, loop);
  /** The world's invitation for the beacon, made again only when the world changes. */
  let invite: Invite | null = null;
  const stopReacting = reactToGame({ game, display, effects, me, loop });

  const pose: PlanetPose = {
    quaternion: world.planet.quaternion,
    radius: display.radius,
    scale: 1,
  };
  /** The welcome planet's own slow turn. */
  const spin = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), 0.45);
  const spinStep = new THREE.Quaternion();
  const point = new THREE.Vector3();
  const screen = screenPoint();
  let frameMs = 0;

  /** Grass around my tile in the game; none on the welcome planet. */
  const placeGrass = (): void => {
    if (!grass) return;
    if (display.mode === 'game' && display.game)
      grass.show(display.game, game.store.get().here, GRASS_REACH, quality.tufts);
    else grass.clear();
  };

  const unsubscribeStore = game.store.subscribe((s, prev) => {
    const now = performance.now();
    if (s.view && s.groups && (s.planetVersion !== prev.planetVersion || display.mode !== 'game')) {
      display.showGame(now, prev.view === null);
      placeGrass();
      loop.wake();
    } else if (!s.view && display.mode === 'game') {
      display.showDemo(now);
      placeGrass();
    }
    if (s.here !== prev.here && display.mode === 'game') placeGrass();
    if (cursor.wants(s, prev)) cursor.show(now);
  });

  const resize = (): void => {
    const { width, height } = renderer.resize();
    rig.resize(width, height);
    world.stars.setPixelRatio(renderer.gl.getPixelRatio());
    projector.setSize(width, height);
    const fov = (world.camera.fov * Math.PI) / 360;
    sparks.setScale((height * renderer.gl.getPixelRatio()) / (2 * Math.tan(fov)));
    loop.wake();
  };
  window.addEventListener('resize', resize);
  resize();
  display.showDemo(performance.now());

  /** The planet turns under my feet in the game, and by itself everywhere else. */
  const turnPlanet = (dt: number, playing: boolean): void => {
    const q = game.local.q;
    if (playing) {
      world.planet.quaternion.set(q[0] ?? 0, q[1] ?? 0, q[2] ?? 0, q[3] ?? 1);
      return;
    }
    if (!reduced) {
      spinStep.setFromAxisAngle(UP, dt * 0.16);
      spin.premultiply(spinStep).normalize();
      loop.wake(100);
    }
    world.planet.quaternion.copy(spin);
  };

  /** The governor stepped down: fewer pixels and tufts, perhaps no painted detail or moving water. */
  const applyQuality = (): void => {
    quality = QUALITY[governor?.tier ?? START_TIER];
    renderer.setLook(style, quality.pixelRatio);
    planet.setDetail(quality.detail);
    setMotion(!reduced && quality.wind, !reduced && quality.movingWater);
    placeGrass();
    resize();
  };

  return {
    frame(dt, now, full) {
      const t0 = performance.now();
      if (governor?.frame(now, full)) applyQuality();
      const s = game.store.get();
      const local = game.local;
      const mode = display.mode;
      const playing = mode === 'game' && s.view !== null;
      turnPlanet(dt, playing);
      if (display.ease(dt, reduced)) loop.wake(100);
      pose.radius = display.radius;
      pose.scale = display.shownRadius / display.radius;
      world.planet.scale.setScalar(pose.scale);
      if (playing) me.place(dt, now, pose);
      const { width, height } = projector;
      const easing = rig.update(dt, {
        viewTarget: mode === 'game' ? local.viewTarget : 1,
        front: mode !== 'game',
        sheet: mode === 'demo' ? 0.76 : 0.42,
        margin: mode === 'demo' ? 1.2 : undefined,
        inset: mode === 'game' ? s.inset : { bottom: 0, right: 0 },
        beacon: mode === 'game' && s.panel === 'hub',
        playing,
        shownRadius: display.shownRadius,
        lift: me.height() * pose.scale,
        width,
        height,
        reduced,
      });
      if (easing) loop.wake(100);
      local.view = rig.state.view;
      if (Math.abs(rig.state.view - (mode === 'game' ? local.viewTarget : 1)) > 0.002)
        loop.wake(100);
      const grid = display.grid;
      const count = playing && grid ? mates.place(now, pose, grid) : 0;
      if (!playing) mates.hide();
      if (s.world && invite?.world !== s.world.id) invite = inviteTo(s.world.id);
      beacon.place(display.game, pose, playing && s.panel === 'hub' ? invite : null);
      figures.setCount(count);
      figures.commit();
      clouds.update(dt, display.shownRadius, true, reduced);
      if (!reduced) loop.wake(40);
      const popping = planet.animate(now);
      const propsMoving = props.animate(now, game.clock.serverNow(), reduced);
      const sparkling = sparks.step(dt);
      if (popping || propsMoving || sparkling || local.moving > 0.01) loop.wake(100);
      world.stars.update(reduced ? 0 : now / 1000, world.planet.quaternion);
      world.camera.updateMatrixWorld();
      if (stylized) updateStyle(now / 1000, world.camera, world.sun);
      planet.cull(world.camera);
      renderer.render(world.scene, world.camera);
      frameMs = performance.now() - t0;
    },
    pickTile(x, y) {
      const s = game.store.get();
      if (!s.view || display.mode !== 'game') return -1;
      const grid = display.gridOf(s.view.subdivisions);
      const { width, height } = projector;
      return pickTile(
        world.camera,
        world.planet.quaternion,
        display.shownRadius,
        grid,
        x,
        y,
        width,
        height,
      );
    },
    myScreenPosition() {
      projector.project(point.copy(me.position).add(MY_MIDDLE), -1, screen);
      return { x: screen.x, y: screen.y };
    },
    fingerScale: () => {
      const r1 = radiusOf(RULES.firstSize);
      const perPixelAtFirst = 1 / (rig.pxPerUnitAt(r1, projector.height) * r1);
      return (perPixelAtFirst * RULES.firstSize) / (display.grid?.subdivisions ?? RULES.firstSize);
    },
    shownRadius: () => display.shownRadius,
    stats: () => ({ ...renderer.stats(), frameMs, quality: governor?.tier ?? style }),
    resize,
    dispose() {
      unsubscribeStore();
      stopReacting();
      window.removeEventListener('resize', resize);
      mates.remove();
      beacon.remove();
      cursor.dispose();
      renderer.dispose();
    },
  };
}
